/**
 * `useTrackedSelector` — read-side "what the user saw" telemetry (Rally US2055264).
 *
 * PROTOTYPE. This is the read/display-side counterpart to the store-side
 * selector-delta walk (`selectorDeltaTelemetryMiddleware`), which it is intended
 * to replace (walk removal tracked in US2055461).
 *
 * Where the walk synthetically re-evaluates every registered selector on every
 * action ("what the value WOULD be at action N"), this hook records a value only
 * when a selector is **actually executed by a component to render** — i.e. what
 * the app truly used. It captures, with zero per-feature effort:
 *
 *   - `component`   — the rendering component, read from the React fiber
 *                     (`ReactCurrentOwner.current.type`), the DevTools technique.
 *   - `field`       — derived from the selector *reference* (WeakMap, seeded from
 *                     the function name); inline/anonymous selectors get a STABLE
 *                     key hashed from their source text (US2055631), never an
 *                     order-dependent counter.
 *   - `value`       — the value the selector returned for this render.
 *   - `actionIndex` — the **last-completed** (call-time) action ordinal — the
 *                     state version the selector actually read. Renders lag
 *                     actions (React batching), so this is the current monotonic
 *                     per-store counter at execution time, NOT the causal/
 *                     triggering action. Do not read it as causal.
 *   - `storeId` / `sessionId` — join keys to line `SelectorRead` up with the
 *                     action-spine streams (US2055631). `actionIndex` is
 *                     per-store, so it is ambiguous without `storeId`.
 *
 * Emission is **change-only**: at most one event per (store, component, field)
 * value change. Any in-between state is reconstructed as "the last time it was
 * selected" — so we count only what the user saw and keep volume sane
 * (`useSelector` runs on every render).
 *
 * NOTE: `useSelector` proving a value was *read for render* is not the same as it
 * being *on screen* (collapsed/off-screen/behind a modal). The visibility layer
 * (IntersectionObserver) is a follow-up in US2055264.
 */
import { useEffect, useRef } from 'react';
import * as ReactRedux from 'react-redux';
import { sessionUtils } from '@makemydeal/dr-shared-ui-utils';

import { getActionIndexCounter, getStoreId } from '@makemydeal/dr-shared-store/middleware/correlationMiddleware';
import { isReduxTelemetryEnabled } from '@makemydeal/dr-shared-store/selectors/featureToggleSelectors';

/** Event name for the read-side stream (contract surface — do not rename casually). */
export const SELECTOR_READ_EVENT = 'SelectorRead';

/**
 * Event name for the dwell stream (US2119069). A second `actionName` on the same
 * `PageAction` type, so it inherits `customattr.coxauto:dealXgId`, `sessionId`,
 * `mmdDealerId` and `environment` from the browser agent for free.
 *
 * WHY A SECOND EVENT. How long a value stayed on screen is only knowable once
 * the NEXT value replaces it, so it cannot be an attribute of the read itself.
 * The two alternatives were both worse: putting the previous value's variance on
 * the successor row writes every variance to NRDB twice and forces the reader to
 * look one row down, and deferring `SelectorRead` itself would move the event
 * off its true timestamp (NR stamps at `addPageAction` time) and lose the final
 * read of every session outright.
 *
 * This row is DELIBERATELY SELF-CONTAINED — it repeats the value, the companion
 * bag and the read's `actionIndex` — because NRQL cannot join it back to its
 * `SelectorRead`: subquery JOIN caps the inner side at 5,000 rows, cannot back
 * an alert condition, and takes only a single-column equality key, while the
 * correlation here is (sessionId, actionIndex, field).
 *
 * Known blind spots — values with no successor never get a dwell row:
 * (1) Last value of a session — no following change arrives. Bound that
 * population from `SelectorRead` instead (`max(timestamp) - min(timestamp
 * of first bad read)` per session).
 * (2) Last value before an unmount — the `useEffect` cleanup in the hook
 * deletes the pending entry on unmount rather than letting it over-report
 * across a remount (e.g. finance → lease → finance deal-type switch). The
 * row is lost; a missing row is better than a wrong number in the field
 * the alert reads.
 */
export const SELECTOR_READ_DWELL_EVENT = 'SelectorReadDwell';

/**
 * Injected `screen` resolver (US2056179, tier 2). `dash-components` is a generic
 * library and must NOT import `dash-store` route selectors directly, so the host
 * app registers a resolver that maps the current Redux state to a user-facing
 * screen label (e.g. `/dashboard` -> `"Dashboard"`). Returns `null`/absent when
 * no resolver is registered — the attribute is then simply omitted and the
 * nerdpack falls back to `section ?? card`.
 *
 * Deliberately screen-only: the breakdown tab axis (`activeBreakdownTabDetails`)
 * is a per-scope GLOBAL, so tagging reads by it would mis-label reads rendered
 * outside the breakdown subtree (e.g. a Programs & Incentives slideout getting
 * "Deal Recap"). The fiber-based `section`/`component` already distinguishes
 * those subtrees, so we keep the injected axis to the unambiguous route.
 */
export type ScreenResolver = (state: unknown) => string | null | undefined;

let screenResolver: ScreenResolver | null = null;

/** Register (or clear, with `null`) the host-app screen resolver. */
export const registerScreenResolver = (resolver: ScreenResolver | null): void => {
    screenResolver = resolver;
};

/** Resolve the current screen label via the injected resolver; never throws. */
const resolveScreen = (state: unknown): string | null => {
    if (!screenResolver) return null;
    try {
        const screen = screenResolver(state);
        return typeof screen === 'string' && screen.length > 0 ? screen : null;
    } catch {
        return null;
    }
};

/**
 * Injected `offerType` axis for read-side telemetry. Same contract as
 * `ScreenResolver`: `dash-components` must not import dash offer selectors, so
 * the host app supplies the mapping.
 *
 * Resolved at EMIT time from live state, deliberately, rather than published
 * once as an NR global custom attribute. Offer type changes mid-session through
 * several paths (deal-type dropdown, DEAL_TYPE_OVERRIDDEN, the unsupported-type
 * override thunk); a published global would freeze at whichever value was
 * current when it was set, and one missed path would silently mislabel every
 * later read. A resolver cannot go stale, and it costs only this stream rather
 * than every PageAction.
 *
 * Matters because the breakdown identity is per offer type: finance closes on
 * amount financed, lease on cap cost / adjusted residual / due at signing. A
 * residual check that cannot tell them apart is not checkable.
 */
export type OfferTypeResolver = (state: unknown) => string | null | undefined;

let offerTypeResolver: OfferTypeResolver | null = null;

/** Register (or clear, with `null`) the host-app offer-type resolver. */
export const registerOfferTypeResolver = (resolver: OfferTypeResolver | null): void => {
    offerTypeResolver = resolver;
};

/**
 * Resolve the current offer type; never throws. Returns `null` when no resolver
 * is registered so the attribute is simply absent, but `'unresolved'` when a
 * resolver IS registered and yields nothing — New Relic drops null-valued
 * attributes, so a null there would hide the gap (same reasoning as the
 * `unresolved:` screen sentinel).
 */
const resolveOfferType = (state: unknown): string | null => {
    if (!offerTypeResolver) return null;
    try {
        const offerType = offerTypeResolver(state);
        return typeof offerType === 'string' && offerType.length > 0 ? offerType : 'unresolved';
    } catch {
        return 'unresolved';
    }
};

/**
 * Injected COMPANION-VALUE resolvers (US2058402). Same contract as the screen
 * and offer-type resolvers above, but returning a bag of related values rather
 * than one axis — and keyed by name, so a read opts into exactly the bag it
 * needs.
 *
 * WHY THIS EXISTS. An invariant spans several selectors, but change-only
 * emission puts them on DIFFERENT events. Editing Cash Down emits
 * `effectiveDownPayment` at one action and `effectiveAmountFinanced` at the
 * next, once the payment service answers; `netSellingPrice`, the trade total
 * and the rebate total do not re-emit at all because they did not change. To
 * check `netSelling - netTrade - rebates - cashDown == amtFinanced` at the
 * moment `amtFinanced` was read, a query would have to carry three values
 * forward from earlier events — and NRQL has no window functions and no
 * self-join, so it cannot. Measured consequence: the check was only ever exact
 * on page load, where all five happen to emit together.
 *
 * The fix is to put the companions ON the read that needs them. Because they
 * are resolved from the SAME `store.getState()` the selector just read, they
 * cannot be stale relative to each other, which makes the check exact with no
 * window and no settle gate:
 *
 *   { field: 'effectiveAmountFinanced', value: 25124.70,
 *     ctx.netSellingPrice: 30124.70, ctx.netTrade: 0, ctx.rebates: 0,
 *     ctx.cashDown: 5000.00 }
 *
 * DELIBERATELY A MECHANISM, NOT THIS INVARIANT. `dash-components` is a generic
 * library and must not import `dash-store` offer selectors, so the host app
 * registers the bag and a call site names it. The lease cap-cost identity
 * (`capCostTotal - capCostReduction == adjustedCapCost`) registers its own bag
 * under its own name and tags its own read; nothing here learns what a deal or
 * an offer is.
 *
 * `arg` is passed through opaquely from `TrackedSelectorOptions.contextArg` so
 * a deal-scoped read (offer-compare columns pass a `deal`) can resolve its
 * companions against the same scope it read itself. Without it a history column
 * would attach the PRIMARY offer's numbers to a historical deal's value and
 * manufacture a variance that does not exist.
 */
export type ContextResolver = (state: unknown, arg?: unknown) => Record<string, unknown> | null | undefined;

const contextResolvers = new Map<string, ContextResolver>();

/** Register (or remove, with `null`) a named companion-value resolver. */
export const registerContextResolver = (name: string, resolver: ContextResolver | null): void => {
    if (resolver) {
        contextResolvers.set(name, resolver);
    } else {
        contextResolvers.delete(name);
    }
};

/** Attribute prefix for companion values, so they can never shadow a first-class attribute. */
export const CONTEXT_ATTR_PREFIX = 'ctx.';

/**
 * Cap on companion values per read. A bag is authored by hand for one invariant
 * — single digits — so this only exists to stop a buggy resolver turning every
 * read into an unbounded payload. Extra keys are dropped, and `contextKeys`
 * reports the true size so the truncation is visible in NR rather than silent.
 */
export const MAX_CONTEXT_KEYS = 12;

/**
 * Resolve a named companion bag; never throws. Values follow the same
 * safe-by-default policy as `value` itself (`resolveEmittedValue`): numbers and
 * booleans raw, strings and objects redacted — an invariant's companions are
 * numeric, so anything else is a mistake worth seeing rather than shipping.
 */
const resolveContext = (name: string | undefined, state: unknown, arg: unknown): Record<string, unknown> => {
    if (!name) return {};
    const resolver = contextResolvers.get(name);
    if (!resolver) return { contextName: name, contextResolved: false, contextKeys: 0 };
    try {
        const bag = resolver(state, arg);
        if (!bag || typeof bag !== 'object') {
            return { contextName: name, contextResolved: false, contextKeys: 0 };
        }
        const entries = Object.entries(bag);
        const attrs: Record<string, unknown> = {
            contextName: name,
            contextResolved: true,
            contextKeys: entries.length
        };
        for (const [key, val] of entries.slice(0, MAX_CONTEXT_KEYS)) {
            attrs[`${CONTEXT_ATTR_PREFIX}${key}`] = isPiiSafeValueType(val) ? val : REDACTED_VALUE;
        }
        return attrs;
    } catch {
        return { contextName: name, contextResolved: false, contextKeys: 0 };
    }
};

/**
 * Derive a stable field key from a selector. Named selectors seed from the
 * function name (`getTotalTax` -> `totalTax`). Inline/anonymous selectors seed
 * from a hash of the selector's **source text** so the key is stable across
 * renders/sessions and distinct inline selectors don't collide — never an
 * order-dependent counter (US2055631). Cached by reference for named selectors;
 * inline selectors (a new reference each render) recompute the same hash.
 */
const fieldKeyBySelector = new WeakMap<object, string>();

/** 32-bit FNV-1a hash rendered as 8 hex chars — tiny, dependency-free, stable. */
const fnv1a8 = (input: string): string => {
    let hash = 0x811c9dc5;
    for (let i = 0; i < input.length; i++) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
};

export const deriveFieldKey = (selector: (state: unknown) => unknown): string => {
    const existing = fieldKeyBySelector.get(selector);
    if (existing) return existing;
    const name = selector.name || '';
    let key: string;
    if (name && name.length > 0) {
        // getTotalTax -> totalTax; isManualFormOpen -> manualFormOpen
        const stripped = name.replace(/^(get|is|select|use)/, '');
        key = stripped.length > 0 ? stripped.charAt(0).toLowerCase() + stripped.slice(1) : name;
    } else {
        // Anonymous/inline: stable, order-independent key from the source text.
        key = `selector_${fnv1a8(selector.toString())}`;
    }
    fieldKeyBySelector.set(selector, key);
    return key;
};

/**
 * Emit a page action, guarding for the NR browser agent not being present.
 */
const emitReadEvent = (name: string, attrs: Record<string, unknown>): void => {
    try {
        const nr = (window as unknown as { newrelic?: { addPageAction?: (name: string, a?: Record<string, unknown>) => void } })
            .newrelic;
        nr?.addPageAction?.(name, attrs);
    } catch {
        /* never let telemetry break render */
    }
};

// Change-only guard: last emitted value SIGNATURE + monotonic emit timestamp,
// per store+component+field. Module-scoped so it survives re-renders; keyed by
// string so equal content dedups. We store a compact SIGNATURE (never the raw
// value) so the cache can never retain a reference to a selector's return
// value — an object/array-valued selector would otherwise keep that object
// alive for the page lifetime (US2057434). The `ts` (a `performance.now()`
// reading captured at the previous emit) powers `msSinceLastChange` (US2057987):
// the dwell of the value being replaced, for flash detection. Entry count is
// bounded (stores × component names × field keys).
const lastEmittedByKey = new Map<string, { sig: string; prevSig?: string; flapCount: number; ts: number }>();

/**
 * How many alternations of a flapping key are emitted before the stream is
 * suppressed (US2058221).
 *
 * A "flap" is a change BACK to the value from two changes ago (A -> B -> A).
 * Two causes, both worth exactly one investigation and zero unbounded streams:
 *
 *  1. An IDENTITY COLLISION — two different selectors sharing one dedup key.
 *     Each read then sees the other's value as "previous", so BOTH report a
 *     change on every render, forever. Observed on `AddsToCapCost`, where
 *     `compositeSelectors.getAddsToCapCostTotal` and
 *     `offerReduxSelectors.getAddsToCapCostTotal` (different selectors, same
 *     NAME) were both keyed `addsToCapCostTotal`: 0 <-> 1005.59 on all 36
 *     consecutive `SOCKET_EVENT_IGNORED` dispatches, 0-11ms apart. The
 *     `identityCollisions` spec now fails the build on this class, so the
 *     runtime guard is the backstop, not the primary defense.
 *  2. A GENUINE product-side flap — an unmemoized selector or real state churn
 *     rendering a wrong intermediate value. Also a defect, also fully described
 *     by the first few alternations.
 *
 * Neither needs more than a handful of samples, and an uncapped flap is a
 * per-render event stream — the exact unbounded-cost failure this hook's
 * change-only design exists to prevent. So: emit the first
 * `FLAP_EMIT_LIMIT` alternations, then one terminal `flapSuppressed` marker so
 * the key stays discoverable in NR, then go silent until a genuinely new value
 * appears (which resets the counter).
 */
export const FLAP_EMIT_LIMIT = 4;

/**
 * Reads awaiting a dwell measurement (US2119069), keyed identically to
 * `lastEmittedByKey`. Holds the already-emitted `SelectorRead` attributes plus
 * the `performance.now()` at which that value went on screen; released as a
 * `SelectorReadDwell` event when the key's next change arrives.
 *
 * Only keys whose call site passed `emitDwell` are tracked, so this stays at one
 * entry per opted-in (store, component, field, instance) rather than mirroring
 * the ~90 tracked selectors.
 *
 * Every stored value has already been through `resolveEmittedValue` and the
 * companion-bag PII gate, and non-scalars are re-substituted on the way in, so
 * this map can never retain a reference into a selector's return value
 * (US2057434) even when a call site opted into `emitValue`.
 */
const pendingDwellByKey = new Map<string, { attrs: Record<string, unknown>; ts: number }>();

/**
 * A compact, stable signature of a selected value for change detection — never
 * the value itself (so the cache retains no object references, US2057434).
 * Primitives map to short `Object.is`-aware tokens (distinguishing `NaN`, `±0`,
 * `null`, `undefined`); strings and objects map to an `fnv1a8` hash of their
 * serialization so a long route string or a large object collapses to 8 hex
 * chars. Cyclic/unserializable objects yield a stable sentinel rather than
 * throwing. A 32-bit hash collision would at most suppress one change emission —
 * acceptable for telemetry across the few values a (component, field) takes.
 */
export const valueSignature = (value: unknown): string => {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    const t = typeof value;
    if (t === 'number') {
        const n = value as number;
        if (Number.isNaN(n)) return 'n:NaN';
        if (Object.is(n, -0)) return 'n:-0';
        return `n:${n}`;
    }
    if (t === 'boolean') return `b:${value as boolean}`;
    if (t === 'string') return `s:${fnv1a8(value as string)}`;
    if (t === 'object') {
        try {
            return `o:${fnv1a8(JSON.stringify(value))}`;
        } catch {
            return 'o:unserializable';
        }
    }
    // function / symbol / bigint — rare as selected values; String() is stable.
    return `x:${String(value)}`;
};

/**
 * Options for `useTrackedSelector` (US2057911). Passed as the 2nd argument when
 * no equality function is needed, or the 3rd argument alongside one.
 */
export type TrackedSelectorOptions = {
    /**
     * Explicit component name for this read. REQUIRED for usable telemetry in a
     * deployed build: React 18 only populates `ReactCurrentOwner.current` under
     * `__DEV__`, so the previous fiber-derived name resolved to `'unknown'` in
     * every production bundle (verified: all react-dom renderers report
     * `bundleType: 0`). A literal here survives minification and is stable
     * across builds. Omitted -> `'unknown'`.
     */
    component?: string;
    /**
     * Explicit field key for this read. REQUIRED for usable telemetry: the
     * fallback derives from `selector.name`, but DR-Dash selectors are imported
     * from published, already-minified `@makemydeal/*` packages, so the derived
     * key arrives mangled (`yS`, `jS`, `qMe`) and changes on every dependency
     * rebuild — breaking any saved NRQL keyed on `field`. Omitted -> derived.
     */
    field?: string;
    /**
     * Discriminator for one INSTANCE of a repeated component. The dedup key is
     * `storeId | component | field | instanceKey`; without the last segment,
     * every row of a list shares one slot and overwrites its siblings, so each
     * overwrite registers as a spurious change. Measured on a two-trade deal:
     * `TradeItem.vin` emitted 46 times, 30 of them within 50ms of each other,
     * while every single-instance field on the same card emitted zero
     * same-cycle reads. Supply anything stable per row — a row index, an id.
     */
    instanceKey?: string | number;
    /**
     * Name of a companion-value bag to attach to this read, registered by the
     * host app with `registerContextResolver`. Emitted as `ctx.<key>` alongside
     * `value`, resolved from the SAME state the selector just read.
     *
     * Opt in only where an invariant is CLOSED by this read — the last term to
     * settle. `AmountFinanced` is that term for the finance breakdown: the
     * payment service answers, `effectiveAmountFinanced` changes, and at that
     * instant every input is final. Tagging an input instead would emit a bag
     * mid-recalc and record a variance that the app was about to fix anyway.
     */
    context?: string;
    /**
     * Opaque value handed to the context resolver as its second argument. Use it
     * to resolve companions in the SAME scope the read used — offer-compare
     * columns pass a `deal`, and without this the bag would come from the
     * primary offer while `value` came from a historical one.
     */
    contextArg?: unknown;
    /**
     * Emit a companion `SelectorReadDwell` event for this read, carrying how long
     * the value was on screen (US2119069).
     *
     * The dwell of a value is only knowable when the next value replaces it, so
     * the event fires one change late while describing the EARLIER read — its
     * `actionIndex`, `value` and companion bag are all as-of that read, and
     * `endedAtActionIndex` records the change that ended it.
     *
     * Opt in only where the on-screen duration of a wrong value is the question
     * being asked, since it roughly doubles this key's event volume.
     */
    emitDwell?: boolean;
    /**
     * Force-redact the emitted `value` regardless of type. When true the
     * `SelectorRead` event carries `REDACTED_VALUE` INSTEAD of the selector's
     * return value — for known PII-bearing selectors whose type would otherwise
     * be emitted (a number that is really an SSN/account id, a boolean that
     * encodes a protected attribute). Change detection is unaffected: the dedup
     * runs against the REAL value's signature (hashed, module-scoped, never
     * emitted), so a redacted read still re-emits exactly when its underlying
     * value changes. Takes precedence over `emitValue`.
     */
    redactValue?: boolean;
    /**
     * Force-emit the raw `value` even for types that are redacted by default
     * (strings, objects, arrays — see the safe-by-default policy on
     * `resolveEmittedValue`). Use ONLY for a selector the caller has vetted as
     * non-PII — a route path, a status/enum label, a small config object. This
     * is an explicit assertion that the value is safe to ship to New Relic;
     * `redactValue` overrides it.
     */
    emitValue?: boolean;
};

/**
 * Emitted in place of a selector's value when it is redacted. A static token —
 * never the raw value, and never a hash of it in the payload — so no PII, nor
 * anything derived from it, leaves the hook. Exported for downstream consumers
 * and tests.
 */
export const REDACTED_VALUE = '‹redacted›';

/**
 * Types that are safe to emit raw by default. `SelectorRead.value` ships to New
 * Relic and this hook is a drop-in `useSelector` applied at ~90 call sites, so
 * the default must be conservative: identifiers/PII (names, addresses, emails,
 * VINs, phone numbers) live in STRINGS and inside OBJECTS/arrays, so those are
 * redacted unless the call site opts in with `emitValue`. Numbers and booleans
 * — payment amounts, term months, flags — are the analytic payload and are not
 * personally identifiable on their own, so they emit raw; a numeric selector
 * that is really an identifier must opt OUT with `redactValue`.
 */
export const isPiiSafeValueType = (value: unknown): boolean => {
    if (value === null || value === undefined) return true;
    const t = typeof value;
    return t === 'number' || t === 'boolean';
};

/** Coarse, non-PII type tag emitted alongside every read so redacted values stay queryable. */
export const describeValueType = (value: unknown): string => {
    if (value === null) return 'null';
    if (Array.isArray(value)) return 'array';
    return typeof value;
};

/**
 * Resolve what to put in the emitted `value` under the safe-by-default policy:
 * `redactValue` (force-redact) wins, then `emitValue` (vetted allow), else the
 * type gate — raw for numbers/booleans/nullish, `REDACTED_VALUE` for
 * strings/objects/everything else.
 */
export const resolveEmittedValue = (value: unknown, opts: { redactValue?: boolean; emitValue?: boolean }): unknown => {
    if (opts.redactValue) return REDACTED_VALUE;
    if (opts.emitValue) return value;
    return isPiiSafeValueType(value) ? value : REDACTED_VALUE;
};

/**
 * Drop-in replacement for react-redux `useSelector` that records the read for
 * telemetry (change-only, gated on `enableReduxTelemetry`). Returns exactly what
 * `useSelector` returns; telemetry is a pure side effect that never affects the
 * value or render.
 *
 * The second argument is either react-redux's equality function (existing API)
 * or a `TrackedSelectorOptions` object; when an equality function is supplied,
 * options come third. Value emission is SAFE-BY-DEFAULT: strings/objects are
 * redacted unless the call site opts in with `emitValue`; numbers/booleans emit
 * raw unless the call site opts out with `redactValue`. See `resolveEmittedValue`
 * and `TrackedSelectorOptions` (US2057911).
 */
export const useTrackedSelector = <TState, TSelected>(
    selector: (state: TState) => TSelected,
    equalityFnOrOptions?: ((left: TSelected, right: TSelected) => boolean) | TrackedSelectorOptions,
    options?: TrackedSelectorOptions
): TSelected => {
    // Disambiguate the overloaded 2nd arg: a function is the equality fn (react-
    // redux API), an object is the options bag; options may also arrive 3rd.
    const equalityFn = typeof equalityFnOrOptions === 'function' ? equalityFnOrOptions : undefined;
    const resolvedOptions = equalityFnOrOptions && typeof equalityFnOrOptions === 'object' ? equalityFnOrOptions : options;
    const redactValue = resolvedOptions?.redactValue === true;
    const emitValue = resolvedOptions?.emitValue === true;

    // Call useSelector with the caller's exact arity: passing `undefined` as a
    // second argument is functionally identical to omitting it, but it would add
    // a spurious arg that breaks tests asserting the exact call shape.
    const value = (ReactRedux.useSelector as (...args: unknown[]) => TSelected)(
        ...(equalityFn ? [selector, equalityFn] : [selector])
    );

    // `useStore` is absent in tests that partial-mock react-redux, and the
    // telemetry it powers is optional, so capability-detect it. In production
    // react-redux always provides it, so hook order is stable per environment.
    let store: { getState: () => unknown } | undefined;
    if (typeof ReactRedux.useStore === 'function') {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        store = ReactRedux.useStore();
    }

    // Tracks the latest dedupKey for the unmount cleanup (US2119069). A ref
    // so the cleanup effect always reads the current key without needing it
    // in the deps array (which would re-run cleanup on every key change and
    // strand a pending dwell unnecessarily).
    const latestDedupKeyRef = useRef<string | null>(null);

    // Clear the pending dwell entry on unmount (US2119069). Without this,
    // a finance → lease deal-type switch (which unmounts <AmountFinanced>)
    // would leave a stranded entry that fires on remount with a dwellMs
    // spanning the entire lease visit — when the value was never on screen.
    // The lost row is the same accepted blind spot as session-end (see the
    // Known-blind-spots note on SELECTOR_READ_DWELL_EVENT).
    // Must sit BEFORE the try-block early return (flap cap) to satisfy
    // react-hooks/rules-of-hooks — hooks cannot follow an early return.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(
        () => () => {
            if (latestDedupKeyRef.current) {
                pendingDwellByKey.delete(latestDedupKeyRef.current);
            }
        },
        []
    );

    // Explicit identity (see `TrackedSelectorOptions`). The fiber-derived
    // component/card/section walk was removed: it can only ever resolve in a
    // DEV React build, so in production it emitted `component: 'unknown'` for
    // every read — which additionally collapsed the dedup key below onto a
    // single slot per field, making distinct components ping-pong each other's
    // values (observed: one field emitting 28x at a 51ms median).
    const explicitComponent = resolvedOptions?.component;
    const explicitField = resolvedOptions?.field;
    const instanceKey = resolvedOptions?.instanceKey;
    const component = explicitComponent ?? 'unknown';
    // 'explicit' = both supplied (the only fully attributable read), 'partial' =
    // one, 'derived' = neither (component defaults and field comes from the
    // minification-sensitive `selector.name` fallback).
    const identitySource =
        explicitComponent && explicitField ? 'explicit' : explicitComponent || explicitField ? 'partial' : 'derived';

    try {
        if (store && isReduxTelemetryEnabled(store.getState() as never)) {
            const storeId = getStoreId(store);
            const field = explicitField ?? deriveFieldKey(selector as (s: unknown) => unknown);
            const actionIndex = getActionIndexCounter(store).value;
            // Dedup per store (US2055631): actionIndex is per-store and the same
            // component|field read against two stores must not cross-dedup.
            // Instance segment (US2055264): a repeated component's rows must not
            // share one slot — see `instanceKey` on TrackedSelectorOptions.
            const dedupKey = `${storeId}|${component}|${field}|${instanceKey ?? ''}`;
            if (resolvedOptions?.emitDwell) {
                latestDedupKeyRef.current = dedupKey;
            }
            const signature = valueSignature(value);
            const previous = lastEmittedByKey.get(dedupKey);
            if (previous?.sig !== signature) {
                // Dwell of the value being replaced (US2057987): elapsed ms since
                // this key's previous emitted change — `null` on the first change
                // per key per page (no prior to measure). Monotonic clock so it's
                // immune to wall-clock skew; RAW ms only — any "faster than a
                // human" threshold lives on the dashboards, never in the client.
                const nowMs = performance.now();
                const msSinceLastChange = previous ? Math.max(0, Math.round(nowMs - previous.ts)) : null;
                // Flap detection (US2058221): a change BACK to the value from two
                // changes ago is an alternation, not progress. Counted per key and
                // RESET by any genuinely new value, so a settled value that later
                // moves on to a third value emits normally. See FLAP_EMIT_LIMIT
                // for why this is capped rather than merely reported.
                // `previous.prevSig` is `undefined` on a key's first change and
                // `signature` is always a string, so the equality check alone
                // rules that case out — no separate `!== undefined` guard, which
                // would be a branch no test could ever take. Narrowing flows from
                // the aliased const, so `previous.flapCount` needs no `?.`/`??`
                // (also unreachable: every cache entry sets `flapCount`).
                const isFlapBack = previous !== undefined && previous.prevSig === signature;
                const flapCount = isFlapBack ? previous.flapCount + 1 : 0;
                lastEmittedByKey.set(dedupKey, {
                    sig: signature,
                    prevSig: previous?.sig,
                    flapCount,
                    ts: nowMs
                });
                // Release the previous value's dwell (US2119069) BEFORE the flap
                // cutoff below: a flap-suppressed change still ends the value it
                // replaces, and returning early first would strand that pending
                // read for the rest of the page.
                const pendingDwell = pendingDwellByKey.get(dedupKey);
                if (pendingDwell) {
                    pendingDwellByKey.delete(dedupKey);
                    emitReadEvent(SELECTOR_READ_DWELL_EVENT, {
                        ...pendingDwell.attrs,
                        dwellMs: Math.max(0, Math.round(nowMs - pendingDwell.ts)),
                        endedAtActionIndex: actionIndex
                    });
                }
                // Past the limit the key is silent: the pattern is already fully
                // described by the emitted samples plus the terminal marker.
                if (flapCount > FLAP_EMIT_LIMIT) {
                    return value;
                }
                // One snapshot for every resolver, so the axes and the companion
                // values describe the same state the selector just read. Calling
                // getState() per resolver would let an interleaved dispatch split
                // them across two versions and fabricate a variance.
                const stateAtRead = store.getState();
                const screen = resolveScreen(stateAtRead);
                const offerType = resolveOfferType(stateAtRead);
                const context = resolveContext(resolvedOptions?.context, stateAtRead, resolvedOptions?.contextArg);
                const emittedValue = resolveEmittedValue(value, { redactValue, emitValue });
                // Shared by both streams so the dwell row describes exactly the
                // read it measures. `...context` stays last for the same reason
                // as below.
                const readAttrs: Record<string, unknown> = {
                    storeId,
                    sessionId: sessionUtils.getAppSessionId(),
                    component,
                    screen,
                    offerType,
                    field,
                    instanceKey: instanceKey ?? null,
                    identitySource,
                    value: emittedValue,
                    valueType: describeValueType(value),
                    actionIndex,
                    ...context
                };
                if (resolvedOptions?.emitDwell) {
                    pendingDwellByKey.set(dedupKey, {
                        // Re-gate the value: `emitValue` can let a raw object
                        // through to `SelectorRead`, and this map outlives the
                        // render, so storing it would retain a reference into the
                        // selector's return value (US2057434).
                        attrs: { ...readAttrs, value: isPiiSafeValueType(emittedValue) ? emittedValue : REDACTED_VALUE },
                        ts: nowMs
                    });
                }
                emitReadEvent(SELECTOR_READ_EVENT, {
                    storeId,
                    sessionId: sessionUtils.getAppSessionId(),
                    component,
                    screen,
                    offerType,
                    field,
                    instanceKey: instanceKey ?? null,
                    // Which identity the read used, so partial adoption is
                    // queryable and nobody mistakes a defaulted `'unknown'`
                    // component for a real one.
                    identitySource,
                    // Value emission is SAFE-BY-DEFAULT (US2057911): strings/
                    // objects are redacted unless the call site vets them with
                    // `emitValue`; numbers/booleans emit raw unless opted out
                    // with `redactValue`. The dedup `signature` above is always
                    // the REAL value's, so change-only emission is unaffected and
                    // no PII leaks. `valueType` is a coarse, non-PII tag so
                    // redacted reads stay queryable in NR.
                    value: emittedValue,
                    valueType: describeValueType(value),
                    actionIndex,
                    // Dwell of the replaced value (US2057987): raw ms the prior
                    // value was active before this change; null on the first
                    // change per key per page. Threshold-free — dashboards decide
                    // what counts as "faster than a human".
                    msSinceLastChange,
                    // Flap instrumentation (US2058221). `flapCount` is how many
                    // consecutive A->B->A alternations this key has taken — 0 for
                    // every healthy read, so `WHERE flapCount > 0` isolates the
                    // defective ones and `max(flapCount) FACET component, field`
                    // ranks them. `flapSuppressed` marks the LAST event a
                    // flapping key emits before going silent, so a suppressed key
                    // is still discoverable rather than just vanishing.
                    flapCount,
                    flapSuppressed: flapCount === FLAP_EMIT_LIMIT,
                    // Observability for the change-detection cache (US2057434):
                    // the live entry count, so growth is queryable in NR via
                    // `max(dedupCacheSize)` per session without a separate beacon.
                    dedupCacheSize: lastEmittedByKey.size,
                    // Companion values (US2058402), spread LAST and namespaced
                    // `ctx.` so a resolver can never shadow a first-class
                    // attribute. Absent entirely unless the call site opted in.
                    ...context
                });
            }
        }
    } catch {
        /* telemetry must never break the component */
    }

    return value;
};

/** TEST-ONLY: reset the change-only dedup map. */
export const __resetTrackedSelectorStateForTests = (): void => {
    lastEmittedByKey.clear();
    pendingDwellByKey.clear();
    screenResolver = null;
    offerTypeResolver = null;
    contextResolvers.clear();
};
